import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { ArrowLeft } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import SpotifyEmbed from "@/components/SpotifyEmbed";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import VideoPlayer from "@/components/VideoPlayer";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setError("Article not found"));
  }, [slug]);

  if (error) return <div className="max-w-[1400px] mx-auto px-5 py-24 text-center text-white/60 font-sub uppercase tracking-[0.2em]" data-testid="article-not-found">{error}</div>;
  if (!article) return <div className="max-w-[1400px] mx-auto px-5 py-24 text-center text-white/40 font-sub uppercase tracking-[0.2em]">Loading…</div>;

  return (
    <article data-testid="article-detail-page">
      <div className="relative h-[60vh] min-h-[400px]">
        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/50 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1100px] mx-auto px-5 lg:px-10 pb-12 w-full">
            <Link to="/magazine" className="inline-flex items-center gap-2 font-sub uppercase tracking-[0.2em] text-xs text-pfc-gold mb-4"><ArrowLeft size={14} /> Back to Magazine</Link>
            <span className="label bg-pfc-gold text-ink-900 px-3 py-1.5 inline-block mb-4">{article.category}</span>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">{article.title}</h1>
            <p className="text-white/70 mt-4 font-body text-base max-w-3xl">{article.excerpt}</p>
            <p className="mt-4 label text-white/50">By {article.author}</p>
          </div>
        </div>
      </div>
      <div className="max-w-[800px] mx-auto px-5 lg:px-10 py-16">
        <div className="text-white/85 text-lg leading-[1.85] font-body whitespace-pre-line">{article.body}</div>

        {article.trailer_url && (
          <VideoPlayer src={article.trailer_url} poster={article.cover_image} title="Watch the official trailer" />
        )}

        {article.youtube_video_id && (
          <YouTubeEmbed videoId={article.youtube_video_id} title="Live performance" />
        )}

        {article.spotify_album_id && (
          <SpotifyEmbed albumId={article.spotify_album_id} title="Stream the album" />
        )}

        {article.spotify_artist_id && (
          <SpotifyEmbed artistId={article.spotify_artist_id} title="Top tracks" />
        )}

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-2">
          {article.tags.map((t) => (
            <span key={t} className="label border border-white/15 text-white/70 px-3 py-1.5">#{t}</span>
          ))}
        </div>

        {/* Inline newsletter signup at end of article */}
        <div className="mt-16 pt-12 border-t border-white/10">
          <div className="label text-pfc-gold mb-3">Stay Connected</div>
          <h3 className="font-display text-3xl text-white uppercase mb-3">Get the next <span className="gold-text">cover story</span> in your inbox.</h3>
          <p className="text-white/60 mb-6 font-body">Subscribe to the PFC weekly. One Sunday-night email. Unsubscribe any time.</p>
          <NewsletterSignup source="article" variant="inline" />
        </div>
      </div>
    </article>
  );
}
